import { useEffect, useRef, useState } from 'react';

export interface MultiSelectOption {
  id: string;
  label: string;
  sublabel?: string;
}

interface Props {
  label: string;
  options: MultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  /** Supplied when the list is server-filtered; omit for a static list. */
  onSearch?: (term: string) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loading?: boolean;
  placeholder?: string;
  emptyHint?: string;
}

/**
 * Checkbox list in a dropdown. Selections that are no longer in `options`
 * (e.g. filtered out by a search) stay selected — the caller owns the full
 * list, so narrowing the view must never silently drop a target.
 */
export function MultiSelectDropdown({
  label,
  options,
  selected,
  onChange,
  onSearch,
  onLoadMore,
  hasMore,
  loading,
  placeholder = 'Search…',
  emptyHint = 'Applies to everyone',
}: Props) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  // Debounced so typing doesn't fire a request per keystroke.
  useEffect(() => {
    if (!onSearch) return;
    const t = setTimeout(() => onSearch(term), 300);
    return () => clearTimeout(t);
  }, [term, onSearch]);

  function toggle(id: string) {
    onChange(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]);
  }

  const visible = onSearch
    ? options
    : options.filter((o) => o.label.toLowerCase().includes(term.toLowerCase()));

  return (
    <div className="relative" ref={rootRef}>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between border border-gray-300 rounded-lg px-3 py-2 text-sm text-left hover:border-gray-400"
      >
        <span className={selected.length ? 'text-gray-900' : 'text-gray-400'}>
          {selected.length ? `${selected.length} selected` : emptyHint}
        </span>
        <span className="text-gray-400 text-xs">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder={placeholder}
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {visible.length === 0 && !loading && (
              <p className="px-3 py-4 text-center text-xs text-gray-400">No matches</p>
            )}
            {visible.map((option) => (
              <label
                key={option.id}
                className="flex items-start gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer select-none"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(option.id)}
                  onChange={() => toggle(option.id)}
                  className="w-4 h-4 accent-indigo-600 mt-0.5"
                />
                <span className="min-w-0">
                  <span className="block truncate">{option.label}</span>
                  {option.sublabel && (
                    <span className="block text-xs text-gray-400 truncate">{option.sublabel}</span>
                  )}
                </span>
              </label>
            ))}
            {loading && <p className="px-3 py-2 text-xs text-gray-400">Loading…</p>}
            {hasMore && !loading && (
              <button
                type="button"
                onClick={onLoadMore}
                className="w-full px-3 py-2 text-xs text-indigo-600 hover:bg-gray-50"
              >
                Load more
              </button>
            )}
          </div>
          {selected.length > 0 && (
            <div className="p-2 border-t border-gray-100">
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Clear selection
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
